import { Button, Stack, Text } from '@ltvco/refresh-lib/theme';
import { nameize } from '@ltvco/refresh-lib/utils';
import { Record, SerializedObituaryDetails } from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchURLParams,
  CollectionType,
  OptionType,
} from '@ltvco/refresh-lib/v2';
import { Tune } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';

import { ReportColumnContainer } from 'components/ChatBV/components';
import { SearchPersonResultsProvider } from 'routes/SearchPerson/context/SearchPersonResultsContext';
import { ObituaryFetcher } from 'routes/SearchPerson/Fetchers/ObituaryFetcher';
import { PersonFetcher } from 'routes/SearchPerson/Fetchers/PersonFetcher';
import { ObituaryResultsContainer } from 'routes/SearchPerson/ResultsContainers/ObituaryResultsContainer';
import { PersonResultsContainer } from 'routes/SearchPerson/ResultsContainers/PersonResultsContainer';
import { viewTypes } from 'routes/SearchPerson/SearchFilter/interfaces';
import SearchHeaderCarousel from 'routes/SearchPerson/SearchFilter/SearchHeader/SearchHeaderCarousel';

type TeaserColumnProps = {
  currentWidth: string;
  isMobileBreakpoint: boolean;
  onClose: () => void;
  searchData: AdvancedSearchURLParams;
  handleOpenFilters: () => void;
  // eslint-disable-next-line no-unused-vars
  handlePersonReport: (bvid: string) => void;
  // eslint-disable-next-line no-unused-vars
  handleObituaryReport: (permalink: string) => void;
};

export const TeaserColumn = ({
  currentWidth,
  isMobileBreakpoint,
  onClose,
  searchData,
  handleOpenFilters,
  handlePersonReport,
  handleObituaryReport,
}: TeaserColumnProps) => {
  const { advancedSearch, source } = searchData;
  const [personResults, setPersonResults] = useState<Record[] | undefined>(
    undefined
  );
  const [selectedOption, setSelectedOption] = useState<CollectionType>(
    CollectionType.PUBLIC_RECORDS
  );
  const [obituaryResults, setObituaryResults] = useState<
    SerializedObituaryDetails | undefined
  >(undefined);
  // const [deathIndexResults, setDeathIndexResults] = useState<
  //   DataCollectionRecord[] | undefined
  // >(undefined);
  // const [contactResults, setContactResults] = useState<
  //   SerializedContactTeaserData | undefined
  // >(undefined);
  // const [familySearchResults, setFamilySearchResults] = useState<
  //   RawFamilySearchPersonResults | undefined
  // >(undefined);

  const [currentView, setCurrentView] = useState<string>(viewTypes.GRID);

  const fetchPerson =
    !advancedSearch || source.includes(CollectionType.PUBLIC_RECORDS);
  const fetchObituary =
    !advancedSearch || source.includes(CollectionType.OBITUARIES);
  // const fetchAncestor =
  //   !advancedSearch || source.includes(CollectionType.FAMILY_SEARCH);
  // const fetchContact =
  //   !advancedSearch || source.includes(CollectionType.CONTACTS);
  // const fetchDeathIndex =
  //   !advancedSearch || source.includes(CollectionType.DEATH_INDEXES);

  const personResultsCount: number = personResults?.length ?? 0;
  const obituaryResultsCount: number = obituaryResults?.length ?? 0;
  // const deathIndexResultsCount: number = deathIndexResults?.length ?? 0;
  // const contactResultsCount: number = contactResults?.total ?? 0;
  // const familySearchResultsCount: number = familySearchResults
  //   ? formattedFamilySearchData(
  //       familySearchResults,
  //       searchData.fName,
  //       searchData.lName
  //     ).length
  //   : 0;

  const capitalizeFullName = nameize(searchData.fullName);

  const fetchers = () => {
    return (
      <>
        {fetchPerson && (
          <PersonFetcher
            setPersonResults={setPersonResults}
            searchData={searchData}
          />
        )}
        {fetchObituary && (
          <ObituaryFetcher
            setObituaryResults={setObituaryResults}
            searchData={searchData}
          />
        )}
        {/* {fetchDeathIndex && (
          <DeathIndexesFetcher
            setDeathIndexResults={setDeathIndexResults}
            searchData={searchData}
          />
        )}
        {fetchAncestor && (
          <FamilySearchFetcher
            setFamilySearchResults={setFamilySearchResults}
            searchData={searchData}
          />
        )}
        {fetchContact && (
          <ContactFetcher
            setContactResults={setContactResults}
            searchData={searchData}
          />
        )} */}
      </>
    );
  };

  const renderResults = () => {
    switch (selectedOption) {
      case CollectionType.OBITUARIES:
        return (
          <ObituaryResultsContainer
            results={obituaryResults}
            searchData={searchData}
          />
        );
      case CollectionType.PUBLIC_RECORDS:
        return <PersonResultsContainer results={personResults} />;
      // case CollectionType.FAMILY_SEARCH:
      //   return (
      //     <FamilySearchResultsContainer
      //       results={familySearchResults}
      //       searchData={searchData}
      //     />
      //   );
      // case CollectionType.CONTACTS:
      //   return <ContactResultsContainer results={contactResults} />;
      // case CollectionType.DEATH_INDEXES:
      //   return (
      //     <CollectionRecordsResultsContainer results={deathIndexResults} />
      //   );
      default:
        return null;
    }
  };

  const formatLength = (length: number | undefined): string => {
    const threshold = 99;
    if (length === undefined || length === null) {
      return '';
    }
    return length > threshold ? '99+' : length.toString();
  };

  const options: OptionType[] = [
    {
      label: 'Public Records',
      value: CollectionType.PUBLIC_RECORDS,
      length: formatLength(personResultsCount),
      visible: fetchPerson,
    },
    // {
    //   label: 'FamilySearch',
    //   value: CollectionType.FAMILY_SEARCH,
    //   length: formatLength(familySearchResultsCount),
    //   visible: fetchAncestor,
    // },
    {
      label: 'Obituary',
      value: CollectionType.OBITUARIES,
      length: formatLength(obituaryResultsCount),
      visible: fetchObituary,
    },
    // {
    //   label: 'Death Indexes',
    //   value: CollectionType.DEATH_INDEXES,
    //   length: formatLength(deathIndexResultsCount),
    //   visible: fetchDeathIndex,
    // },
    // {
    //   label: 'Contact',
    //   value: CollectionType.CONTACTS,
    //   length: formatLength(contactResultsCount),
    //   visible: fetchContact,
    // },
  ];

  return (
    <ReportColumnContainer
      spacing={2}
      minWidth={currentWidth}
      width="40%"
      height="98%"
      sx={{
        px: 4,

        ...(isMobileBreakpoint && {
          animation: 'fadeInScale 0.4s ease-out',
          '@keyframes fadeInScale': {
            '0%': {
              transform: 'scale(0.95)',
              opacity: 0,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        }),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
          padding: '16px 20px',
        }}
      >
        <Text variant="body1" fontWeight="bold" color="text.primary">
          People Search
        </Text>
        <CloseIcon
          onClick={onClose}
          sx={{ color: 'text.secondary', cursor: 'pointer' }}
        />
      </Stack>
      <Button
        type="button"
        variant="outlined"
        onClick={handleOpenFilters}
        fullWidth
      >
        <Text variant="h3" sx={{ mb: 1 }}>
          <Tune sx={{ m: 0.75, mb: -0.75 }} />
          Search & filter
        </Text>
      </Button>
      <SearchHeaderCarousel
        options={options}
        selectedOption={selectedOption}
        setSelectedOption={(value) =>
          setSelectedOption(value.value as CollectionType)
        }
        isChatPage={true}
      />
      <Stack flexDirection="row" alignItems="center" pb={2}>
        <Text
          variant="h3"
          sx={{
            fontWeight: 'normal',
            marginRight: '8px',
            marginLeft: '16px',
            marginBottom: '0',
          }}
        >
          Results for:
        </Text>
        <Text variant="h3" m={0}>
          {capitalizeFullName}
        </Text>
      </Stack>
      <SearchPersonResultsProvider
        value={{
          currentView,
          setCurrentView,
          isOnMobileCardInDesktopVariation: false,
          isChatPage: true,
          handlePersonReport,
          handleObituaryReport,
        }}
      >
        {fetchers()}
        {renderResults()}
      </SearchPersonResultsProvider>
    </ReportColumnContainer>
  );
};
